/**
 * knowledgeSnapshot — turn the Knowledge view's on-screen corpus stats into a
 * human-readable SpaContext snapshot (2026-07-01, see+control generalization).
 *
 * Lets the voice ("BRIDGE") and Pi agents SEE this tenant's knowledge corpus
 * (per-service availability + item counts, and which nav pane is open) instead
 * of guessing. Pure + framework-free so it unit-tests directly.
 */
import type { SpaContext } from '@/stores/spaContextStore';
import type { KnowledgeServiceStats, SynapseStatusResponse } from '@/services/knowledgeApi';

export const KNOWLEDGE_VIEW_ID = 'knowledge';

/** Build the on-screen snapshot for the Knowledge view. `selectedNav` is the
 * open sidebar pane ('overview' or a service key). `synapse` (2026-07-02) is
 * the loop status from its own route — appended as an extra item when loaded
 * so agents SEE the compile state alongside the corpora. */
export function buildKnowledgeSnapshot(
  overview: KnowledgeServiceStats[] | null,
  selectedNav: string,
  synapse: SynapseStatusResponse | null = null,
): SpaContext {
  const services = overview ?? [];
  const items = services.map((s) => {
    const label = s.label ?? s.service;
    if (!s.available) return `${label} — unavailable${s.error ? ` (${s.error})` : ''}`;
    const count = s.count == null ? 'unknown' : String(s.count);
    return `${label} — ${count} item${s.count === 1 ? '' : 's'}`;
  });
  if (synapse) {
    items.push(
      synapse.available
        ? `THE SYNAPSE (knowledge loop) — ${synapse.article_count ?? 'unknown'} compiled article${synapse.article_count === 1 ? '' : 's'}, last compiled ${synapse.last_compiled_at ?? 'never'}`
        : `THE SYNAPSE (knowledge loop) — unavailable${synapse.error ? ` (${synapse.error})` : ''}`,
    );
  }
  const availableCount = services.filter((s) => s.available).length;
  return {
    view: KNOWLEDGE_VIEW_ID,
    activeView: selectedNav,
    title: 'Knowledge',
    detail:
      services.length === 0
        ? 'Knowledge corpus overview is still loading'
        : `${availableCount} of ${services.length} knowledge services available; viewing "${selectedNav}"`,
    items,
  };
}
